// components/ContactPage.js - Updated with LandingPage header and footer
import React, { useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import { 
  Mail, 
  Clock, 
  MapPin, 
  Send,
  CheckCircle,
  AlertCircle,
  Loader2,
  Shield,
  Headphones,
  Globe
} from 'lucide-react';
import { SiteHeader, SiteFooter, SiteStyles } from './site/SiteChrome';

const ContactPage = () => {
  // Public pages use the terminal theme (matches LandingPage).
  useTheme();
  const darkMode = true;
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    
    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      // Send to backend API
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        setSuccess(true);
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: ''
        });
        
        // Hide success message after 5 seconds
        setTimeout(() => {
          setSuccess(false);
        }, 5000);
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      console.error('Contact form error:', error);
      setErrors({
        submit: 'Failed to send message. Please try again or email us directly.'
      });
    } finally {
      setLoading(false);
    }
  };

  const contactMethods = [
    {
      icon: Mail,
      title: 'Email Support',
      description: 'Get help via email',
      value: 'support@tunasignal.ai',
      link: 'mailto:support@tunasignal.ai',
      color: 'blue'
    },
    {
      icon: Clock,
      title: 'Business Hours',
      description: 'When we\'re available',
      value: 'Mon-Fri: 9AM-5PM EST',
      color: 'green'
    },
    {
      icon: Headphones,
      title: 'Response Time',
      description: 'We\'ll get back to you',
      value: 'Within 24-48 hours',
      color: 'sky'
    }
  ];

  return (
    <div className={`ts-page min-h-screen transition-colors ${darkMode ? 'bg-[#07131F]' : 'bg-slate-50'}`}>
      <SiteStyles />
      <SiteHeader active="contact" />

      {/* Main Content */}
      <div className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-12 max-w-3xl">
            <h1 className={`ts-display text-4xl md:text-5xl mb-4 ${
              darkMode ? 'text-white' : 'text-slate-900'
            }`}>
              Get in touch
            </h1>
            
            <p className={`text-lg max-w-2xl ${
              darkMode ? 'text-slate-400' : 'text-slate-600'
            }`}>
              Questions about TunaSignal AI, your account or billing? Send us a message
              and we'll reply within one to two business days.
            </p>
          </div>

          {/* Contact Methods Cards */}
          <div className="grid md:grid-cols-3 mb-12 border-t border-[#28445F]">
            {contactMethods.map((method, index) => (
              <div
                key={index}
                className={`py-6 md:pr-8 border-b md:border-b-0 border-[#1C3348] ${index ? 'md:pl-8 md:border-l' : ''}`}
              >
                
                <h3 className={`text-lg font-semibold mb-2 ${
                  darkMode ? 'text-white' : 'text-slate-900'
                }`}>
                  {method.title}
                </h3>
                
                <p className={`text-sm mb-3 ${
                  darkMode ? 'text-slate-400' : 'text-slate-600'
                }`}>
                  {method.description}
                </p>
                
                {method.link ? (
                  <a
                    href={method.link}
                    className={`text-sm font-medium ${
                      method.color === 'blue'
                        ? 'text-[#5AA9F0] hover:text-[#8CC4F5]'
                        : method.color === 'green'
                        ? 'text-emerald-400 hover:text-emerald-300'
                        : 'text-sky-400 hover:text-sky-300'
                    }`}
                  >
                    {method.value}
                  </a>
                ) : (
                  <p className={`text-sm font-medium ${
                    darkMode ? 'text-slate-300' : 'text-slate-700'
                  }`}>
                    {method.value}
                  </p>
                )}
              </div>
            ))}
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Contact Form */}
            <div className={`${
              darkMode ? 'bg-[#0D1E2E]' : 'bg-white'
            } rounded-md border border-[#1C3348] p-8`}>
              <h2 className={`ts-display-sub text-2xl mb-6 ${
                darkMode ? 'text-white' : 'text-slate-900'
              }`}>
                Send us a Message
              </h2>

              {/* Success Message */}
              {success && (
                <div className="mb-6 p-4 bg-emerald-400/10 border border-emerald-400/30 rounded-md" role="status">
                  <div className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-emerald-400 mr-3" />
                    <div>
                      <p className="text-emerald-200 font-medium">
                        Message sent successfully!
                      </p>
                      <p className="text-emerald-300/80 text-sm mt-1">
                        We'll get back to you within 24-48 hours.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Error Message */}
              {errors.submit && (
                <div className="mb-6 p-4 bg-[#F0414F]/10 border border-[#F0414F]/30 rounded-md" role="alert">
                  <div className="flex items-center">
                    <AlertCircle className="w-5 h-5 text-[#FF7A85] mr-3" />
                    <p className="text-red-200">{errors.submit}</p>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name Field */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    darkMode ? 'text-slate-300' : 'text-slate-700'
                  }`}>
                    Your Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="John Doe"
                    className={`w-full px-4 py-3 rounded-md border transition-colors ${
                      errors.name
                        ? 'bg-[#07131F] text-white border-[#F0414F] focus:ring-[#F0414F]/30'
                        : darkMode
                        ? 'bg-[#07131F] border-[#28445F] text-white placeholder-slate-500 focus:border-[#5AA9F0] focus:ring-[#5AA9F0]/30'
                        : 'bg-white border-slate-300 focus:border-blue-500'
                    } focus:outline-none focus:ring-2`}
                  />
                  {errors.name && (
                    <p className="text-[#FF7A85] text-sm mt-1">{errors.name}</p>
                  )}
                </div>

                {/* Email Field */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    darkMode ? 'text-slate-300' : 'text-slate-700'
                  }`}>
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@example.com"
                    className={`w-full px-4 py-3 rounded-md border transition-colors ${
                      errors.email
                        ? 'bg-[#07131F] text-white border-[#F0414F] focus:ring-[#F0414F]/30'
                        : darkMode
                        ? 'bg-[#07131F] border-[#28445F] text-white placeholder-slate-500 focus:border-[#5AA9F0] focus:ring-[#5AA9F0]/30'
                        : 'bg-white border-slate-300 focus:border-blue-500'
                    } focus:outline-none focus:ring-2`}
                  />
                  {errors.email && (
                    <p className="text-[#FF7A85] text-sm mt-1">{errors.email}</p>
                  )}
                </div>

                {/* Subject Field */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    darkMode ? 'text-slate-300' : 'text-slate-700'
                  }`}>
                    Subject
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder="How can we help?"
                    className={`w-full px-4 py-3 rounded-md border transition-colors ${
                      errors.subject
                        ? 'bg-[#07131F] text-white border-[#F0414F] focus:ring-[#F0414F]/30'
                        : darkMode
                        ? 'bg-[#07131F] border-[#28445F] text-white placeholder-slate-500 focus:border-[#5AA9F0] focus:ring-[#5AA9F0]/30'
                        : 'bg-white border-slate-300 focus:border-blue-500'
                    } focus:outline-none focus:ring-2`}
                  />
                  {errors.subject && (
                    <p className="text-[#FF7A85] text-sm mt-1">{errors.subject}</p>
                  )}
                </div>

                {/* Message Field */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    darkMode ? 'text-slate-300' : 'text-slate-700'
                  }`}>
                    Message
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us more about your inquiry..."
                    rows={6}
                    className={`w-full px-4 py-3 rounded-md border transition-colors resize-none ${
                      errors.message
                        ? 'bg-[#07131F] text-white border-[#F0414F] focus:ring-[#F0414F]/30'
                        : darkMode
                        ? 'bg-[#07131F] border-[#28445F] text-white placeholder-slate-500 focus:border-[#5AA9F0] focus:ring-[#5AA9F0]/30'
                        : 'bg-white border-slate-300 focus:border-blue-500'
                    } focus:outline-none focus:ring-2`}
                  />
                  {errors.message && (
                    <p className="text-[#FF7A85] text-sm mt-1">{errors.message}</p>
                  )}
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className={`w-full py-3 px-6 rounded-md font-medium transition-all duration-200 flex items-center justify-center ${
                    loading
                      ? 'bg-[#13283C] text-slate-500 cursor-not-allowed'
                      : 'bg-[#5AA9F0] hover:bg-[#8CC4F5] text-[#07131F] font-semibold'
                  }`}
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Company Information */}
            <div className="space-y-8">
              {/* Office Address */}
              <div className={`${
                darkMode ? 'bg-[#0D1E2E]' : 'bg-white'
              } rounded-md border border-[#1C3348] p-8`}>
                <h2 className={`ts-display-sub text-2xl mb-6 ${
                  darkMode ? 'text-white' : 'text-slate-900'
                }`}>
                  Company Information
                </h2>

                <div className="space-y-6">
                  <div>
                    <div className="flex items-start space-x-3">
                      <MapPin className={`w-5 h-5 mt-1 ${
                        darkMode ? 'text-slate-400' : 'text-slate-500'
                      }`} />
                      <div>
                        <h3 className={`font-semibold mb-2 ${
                          darkMode ? 'text-white' : 'text-slate-900'
                        }`}>
                          Registered Address
                        </h3>
                        <p className={`text-sm ${
                          darkMode ? 'text-slate-400' : 'text-slate-600'
                        }`}>
                          TunaSignal AI, Wyoming LLC<br />
                          Northwest Registered Agent Service Inc<br />
                          30 N Gould St Ste N<br />
                          Sheridan, WY 82801<br />
                          United States
                        </p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-start space-x-3">
                      <Globe className={`w-5 h-5 mt-1 ${
                        darkMode ? 'text-slate-400' : 'text-slate-500'
                      }`} />
                      <div>
                        <h3 className={`font-semibold mb-2 ${
                          darkMode ? 'text-white' : 'text-slate-900'
                        }`}>
                          Service Coverage
                        </h3>
                        <p className={`text-sm ${
                          darkMode ? 'text-slate-400' : 'text-slate-600'
                        }`}>
                          We provide services globally to traders and investors worldwide.
                          Our AI-powered signals cover major US stock markets.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-start space-x-3">
                      <Shield className={`w-5 h-5 mt-1 ${
                        darkMode ? 'text-slate-400' : 'text-slate-500'
                      }`} />
                      <div>
                        <h3 className={`font-semibold mb-2 ${
                          darkMode ? 'text-white' : 'text-slate-900'
                        }`}>
                          Security & Privacy
                        </h3>
                        <p className={`text-sm ${
                          darkMode ? 'text-slate-400' : 'text-slate-600'
                        }`}>
                          Your data is protected with enterprise-grade encryption.
                          We never share your personal information with third parties.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* FAQ Section */}
              <div className={`${
                darkMode ? 'bg-[#0D1E2E]' : 'bg-white'
              } rounded-md border border-[#1C3348] p-8`}>
                <h2 className={`ts-display-sub text-2xl mb-6 ${
                  darkMode ? 'text-white' : 'text-slate-900'
                }`}>
                  Frequently Asked Questions
                </h2>

                <div className="space-y-4">
                  <div>
                    <h3 className={`font-semibold mb-2 ${
                      darkMode ? 'text-white' : 'text-slate-900'
                    }`}>
                      How quickly will I receive a response?
                    </h3>
                    <p className={`text-sm ${
                      darkMode ? 'text-slate-400' : 'text-slate-600'
                    }`}>
                      We typically respond within 24-48 hours during business days.
                    </p>
                  </div>

                  <div>
                    <h3 className={`font-semibold mb-2 ${
                      darkMode ? 'text-white' : 'text-slate-900'
                    }`}>
                      What's the best way to reach support?
                    </h3>
                    <p className={`text-sm ${
                      darkMode ? 'text-slate-400' : 'text-slate-600'
                    }`}>
                      Email is the fastest way to reach us at support@tunasignal.ai
                    </p>
                  </div>

                  <div>
                    <h3 className={`font-semibold mb-2 ${
                      darkMode ? 'text-white' : 'text-slate-900'
                    }`}>
                      Do you offer phone support?
                    </h3>
                    <p className={`text-sm ${
                      darkMode ? 'text-slate-400' : 'text-slate-600'
                    }`}>
                      Currently, we provide support via email to ensure detailed and documented responses.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <SiteFooter />
    </div>
  );
};

export default ContactPage;